import React from "react";
import { TIERS } from "@/lib/pricing";
import { Shield } from "lucide-react";
import { centsToUSD, shortDate } from "@/lib/dates";
import QueryScheduleCard from "./QueryScheduleCard";
import CriticalDatesCard from "./CriticalDatesCard";
import BankruptcyCard from "./BankruptcyCard";
import { JudgmentInsightPanel } from "@/components/debtors/JudgmentInsightPanel";
import { MasterTimelineBar } from "@/components/debtors/MasterTimelineBar";
import { CountdownClocks } from "@/components/debtors/CountdownClocks";
import { ActionChecklistCard } from "@/components/debtors/ActionChecklistCard";
import { StateIntelligenceCard } from "@/components/debtors/StateIntelligenceCard";
import { MultiStateEnforcementCalendar } from "@/components/debtors/MultiStateEnforcementCalendar";
import { variantToCourtLevelHint } from "@/hooks/useJudgmentLaw";
import { cn } from "@/lib/utils";

const Section = ({ title, children, testid }) => (
  <section className="space-y-3" data-testid={testid}>
    <div>
      <div className="text-accent-pink font-semibold text-[13px] tracking-[0.08em] uppercase">{title}</div>
      <div className="mt-2 h-px bg-accent-pink/20" />
    </div>
    <div className="space-y-3">{children}</div>
  </section>
);

const Field = ({ label, value, mono = false, className = "" }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="text-text-secondary text-[13px]">{label}</div>
    <div className={cn("text-white text-[14px] font-medium text-right max-w-[65%]", mono && "jea-mono", className)}>
      {value ?? "—"}
    </div>
  </div>
);

const addressLine = (a) => {
  if (!a) return "—";
  const parts = [];
  if (a.street) parts.push(a.street);
  const cityBits = [a.city, a.state].filter(Boolean).join(", ");
  const zip = a.zip || "";
  const tail = [cityBits, zip].filter(Boolean).join(" ");
  if (tail) parts.push(tail);
  return parts.filter(Boolean).join(" · ") || "—";
};

const OverviewTab = ({ debtor, onSkipQuery, onRunQuery }) => {
  const j = debtor.judgment || {};
  const assets = debtor.assets || {};
  const interest = debtor.interest || {};
  const tier = TIERS.find((t) => t.key === debtor.tier) || TIERS[0];

  const jlPayload = {
    state: j.state,
    date_entered: j.date_entered,
    court_level_variant: j.court_level_variant,
    lien_filed: !!j.lien_filed,
    lien_filed_at: j.lien_filed_at,
    known_real_property_states: j.known_real_property_states || [],
  };
  // Resolve variant_key for the State Intelligence Card. The frontend uses
  // (state 2-letter + court_level_variant hint) → same rules the API applies.
  const guessVariantKey = () => {
    if (!j.state) return null;
    const map = {
      post_2013: "AZ_POST_2013",
      pre_2013_unrenewed: "AZ_PRE_2013_UNRENEWED",
      district: "CO_DISTRICT",
      county: "CO_COUNTY",
      superior: "CT_SUPERIOR",
      small_claims: "CT_SMALL_CLAIMS",
      pre_2021: "VA_PRE_2021",
      post_2021: "VA_POST_2021",
    };
    if (j.court_level_variant && map[j.court_level_variant]) return map[j.court_level_variant];
    // Default variant per state
    const defaults = { AZ: "AZ_POST_2013", CO: "CO_DISTRICT", CT: "CT_SUPERIOR", VA: "VA_POST_2021" };
    return defaults[j.state] || j.state;
  };
  const variantKey = guessVariantKey();

  return (
    <div className="space-y-6" data-testid="debtor-overview-tab">
      {j.state && j.date_entered && (
        <div data-testid="overview-insight-panel">
          <JudgmentInsightPanel
            payload={jlPayload}
            debtorName={debtor.full_name}
            judgmentAmountLabel={j.amount_cents ? centsToUSD(j.amount_cents) : undefined}
          />
        </div>
      )}
      {j.state && j.date_entered && (
        <>
          <MasterTimelineBar payload={jlPayload} />
          <CountdownClocks payload={jlPayload} />
          <ActionChecklistCard debtorId={debtor.id} />
          <StateIntelligenceCard variantKey={variantKey} payload={jlPayload} />
          <MultiStateEnforcementCalendar debtorId={debtor.id} payload={jlPayload} />
        </>
      )}
      <div className="grid grid-cols-[3fr_2fr] gap-6">
      <div className="space-y-6 min-w-0">
        <Section title="Basic Information" testid="section-basic">
          <Field label="Full Name" value={debtor.full_name} />
          <Field label="Aliases" value={(debtor.aliases || []).filter(Boolean).join(", ") || "—"} />
          <Field label="Date of Birth" value={debtor.dob ? shortDate(debtor.dob) : "—"} mono />
          <Field label="SSN (Last 4)" value={debtor.ssn_last4 ? `••••${debtor.ssn_last4}` : "—"} mono />
        </Section>

        <Section title="Address History" testid="section-address">
          <Field label="Current Address" value={addressLine(debtor.current_address)} />
          {(debtor.prior_addresses || []).length > 0 && (
            <div className="pt-2">
              <div className="text-text-secondary text-[13px] mb-1">Prior Addresses</div>
              <ul className="list-disc list-inside space-y-1 text-white text-[13px]">
                {(debtor.prior_addresses || []).map((a, i) => (
                  <li key={i}>{addressLine(a)}</li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        <Section title="Employment" testid="section-employment">
          <Field label="Current Employer" value={debtor.current_employer?.name || "—"} />
          <Field label="Employer Address" value={addressLine(debtor.current_employer?.address)} />
          <Field
            label="Employment Status"
            value={(debtor.employment_status || "unknown").replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
          />
          {(debtor.prior_employers || []).length > 0 && (
            <div className="pt-2">
              <div className="text-text-secondary text-[13px] mb-1">Prior Employers</div>
              <ul className="list-disc list-inside space-y-1 text-white text-[13px]">
                {(debtor.prior_employers || []).map((emp, i) => (
                  <li key={i}>{emp.name || "—"}{emp.address?.city ? ` · ${addressLine(emp.address)}` : ""}</li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        <Section title="Known Assets" testid="section-assets">
          {(assets.real_property || []).length > 0 && (
            <div>
              <div className="text-text-secondary text-[13px] mb-1">Real Property</div>
              <ul className="space-y-1 text-white text-[13px]">
                {assets.real_property.map((rp, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span>{rp.address || "—"}</span>
                    <span className="jea-money">{centsToUSD(rp.estimated_value_cents || 0)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(assets.vehicles || []).length > 0 && (
            <div>
              <div className="text-text-secondary text-[13px] mb-1">Vehicles</div>
              <ul className="space-y-1 text-white text-[13px]">
                {assets.vehicles.map((v, i) => (
                  <li key={i}>
                    {[v.year, v.make, v.model].filter(Boolean).join(" ") || "—"}
                    {v.vin && <span className="jea-mono text-text-secondary ml-2">VIN {v.vin}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(assets.bank_accounts || []).length > 0 && (
            <div>
              <div className="text-text-secondary text-[13px] mb-1">Bank Accounts</div>
              <ul className="space-y-1 text-white text-[13px]">
                {assets.bank_accounts.map((b, i) => (
                  <li key={i}>{b.institution || "—"}{b.last4 ? ` · ••••${b.last4}` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          {assets.other && (
            <div>
              <div className="text-text-secondary text-[13px] mb-1">Other</div>
              <p className="text-white text-[13px] whitespace-pre-line">{assets.other}</p>
            </div>
          )}
          {!(assets.real_property?.length || assets.vehicles?.length || assets.bank_accounts?.length || assets.other) && (
            <div className="text-text-muted text-[13px]">No known assets yet.</div>
          )}
        </Section>

        <Section title="Judgment Details" testid="section-judgment">
          <Field label="Case Number" value={j.case_number || "—"} mono />
          <Field label="Court Name" value={j.court_name || "—"} />
          <Field label="Court Level" value={j.court_level || "—"} />
          <Field label="State" value={j.state || "—"} />
          <Field label="Date Entered" value={j.date_entered ? shortDate(j.date_entered) : "—"} mono />
          <Field
            label="Judgment Amount"
            value={centsToUSD(j.amount_cents || 0)}
            mono
            className="!text-accent-pink !text-[16px] !font-bold"
          />
          <Field label="Interest Rate" value={j.interest_rate != null ? `${j.interest_rate}%` : "—"} mono />
          <Field label="Nature of Debt" value={j.nature_of_debt || "—"} />
          <div className="pt-2 mt-2 border-t border-border-default space-y-2">
            <Field
              label="Accrued Interest to Date"
              value={centsToUSD(interest.accrued_cents || 0)}
              mono
              className="!text-success !font-semibold"
            />
            <Field
              label="Current Total Value"
              value={centsToUSD(interest.current_total_cents || 0)}
              mono
              className="!text-accent-pink !text-[16px] !font-bold"
            />
            {interest.since_date && (
              <div className="text-[11px] text-text-muted text-right">
                Since {shortDate(interest.since_date)} · {interest.days_elapsed} days · ~{(centsToUSD(interest.daily_rate_cents || 0))}/day
              </div>
            )}
          </div>
        </Section>

        <div className="bg-bg-tertiary rounded-[8px] px-4 py-3 inline-flex items-center gap-2" data-testid="overview-tier-summary">
          <Shield size={14} style={{ color: tier.color }} />
          <span className="text-[13px] text-text-secondary">Query tier:</span>
          <span className="text-[13px] font-bold tracking-[0.08em]" style={{ color: tier.color }}>{tier.label}</span>
          <span className="text-[12px] text-text-muted">· every {(tier.frequency || "").replace("every ", "")}</span>
        </div>
      </div>

      <aside className="space-y-6">
        <QueryScheduleCard debtor={debtor} onSkip={onSkipQuery} onRunNow={onRunQuery} />
        <CriticalDatesCard criticalDates={debtor.critical_dates} />
        <BankruptcyCard debtor={debtor} />
      </aside>
      </div>
    </div>
  );
};

export default OverviewTab;
